import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { StyleField } from '@/components/inspector-fields/StyleField/StyleField';
import { SizeField } from '@/components/inspector-fields/SizeField/SizeField';
import { ColorField } from '@/components/inspector-fields/ColorField/ColorField';
import { SliderField } from '@/components/inspector-fields/SliderField/SliderField';
import { PaddingMarginFields } from '@/components/inspector-fields/PaddingMarginFields/PaddingMarginFields';
import StylesFontWeightSettings from '@/components/dropdowns/SelectField/StylesFontWeightSettings';
import StylesFontFamilySettings from '@/components/dropdowns/SelectField/StylesFontFamilySettings';
import type { Button } from '@builder4/shared/types/styles';
import { type ButtonSize, type ButtonType } from '@builder4/shared/styles/constants';
import type { Color, RGBA } from '@builder4/shared/types/layout';
import { checkIsGradient, isColorReference } from '@builder4/shared/utils/styleHelpers';
import { useColor } from '@/hooks/useColor';
import { getButtonHeight, getButtonTypeFromRadius } from '@/utils/helperFunc';
import { useAppSelector } from '@/store/hooks';
import { selectEditingStyleColors } from '@/store/globalStyles/selectors';
import { createPaletteFromGlobalColors } from '@/utils/colorPaletteHelpers';

/** Normalizes a color picker value to a border color: RGBA, palette ref string, or null. Rejects gradients. */
function normalizeBorderColor(color: Color | string | null | undefined): RGBA | string | null {
  if (color === null || color === undefined) return null;
  if (typeof color === 'string' && isColorReference(color)) return color;
  if (!checkIsGradient(color)) return color as RGBA;
  return null;
}

interface StylesInspectorButtonProps {
  className: string;
  onClick?: () => void;
  text?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onUpdate: (data: Button) => void;
  currentData: Button;
  onSizeChange?: (size: ButtonSize) => void;
  onTypeChange?: (type: ButtonType) => void;
}

export const StylesInspectorButton: React.FC<StylesInspectorButtonProps> = ({
  className,
  onClick,
  onUpdate,
  currentData,
  onSizeChange,
  onTypeChange,
}) => {
  const styleColors = useAppSelector(selectEditingStyleColors);
  const styleColorsOrUndefined = styleColors ?? undefined;
  const palettesForPicker = useMemo(
    () => (styleColors ? [createPaletteFromGlobalColors(styleColors)] : []),
    [styleColors],
  );
  // Local UI state (pure, no Craft dependencies)
  const [buttonType, setButtonType] = useState<ButtonType | undefined>('square');
  const [buttonSize, setButtonSize] = useState<ButtonSize>('medium');
  const [fontSize, setFontSize] = useState<number>(14);
  const [spacing, setSpacing] = useState<number>(0);
  const [stroke, setStroke] = useState<number>(0);
  const [currentWeight, setCurrentWeight] = useState<string>('400');
  const [selectedFontFamily, setSelectedFontFamily] = useState<string>('Inter');

  // Padding/Margin/Corners state
  type PMValues = { all: number; top: number; right: number; bottom: number; left: number };
  type PMValuesBySize = { small: PMValues; medium: PMValues; large: PMValues };
  const defaultPMValues: PMValues = { all: 0, top: 0, right: 0, bottom: 0, left: 0 };
  const [paddingVals, setPaddingVals] = useState<PMValuesBySize>({
    small: { ...defaultPMValues },
    medium: { ...defaultPMValues },
    large: { ...defaultPMValues },
  });
  const [marginVals, setMarginVals] = useState<PMValuesBySize>({
    small: { ...defaultPMValues },
    medium: { ...defaultPMValues },
    large: { ...defaultPMValues },
  });
  const [cornerVals, setCornerVals] = useState<PMValuesBySize>({
    small: { ...defaultPMValues },
    medium: { ...defaultPMValues },
    large: { ...defaultPMValues },
  });

  const maxRadius = 64;

  // Helper function to calculate variant based on values
  const calculateVariant = (vals: PMValues): 'locked' | 'unlocked' => {
    let hasSame = !!vals.all;
    if (!hasSame) {
      // Check if all individual values are equal
      hasSame = vals.top === vals.right && vals.right === vals.bottom && vals.bottom === vals.left;
    }
    return hasSame ? 'locked' : 'unlocked';
  };

  useEffect(() => {
    if (currentData) {
      setBackgroundColor(currentData.backgroundColor ?? null);
      // fontColor is required, ensure it's never null/undefined
      setTextColor(currentData.fontColor || ({ r: 0, g: 0, b: 0, a: 1 } as Color));
      setFillHoverColor(currentData.backgroundHoverColor ?? null);
      setTextHoverColor(currentData.fontHoverColor ?? null);

      // Border colors: store as Color (RGBA or string, no gradients supported)
      setBorderColor(currentData.borderColor ?? null);
      setBorderHoverColor(currentData.borderHoverColor ?? null);
      setStroke(currentData.borderWidth || 0);
      setSpacing(currentData.letterSpacing || 0);
      setCurrentWeight(currentData.fontWeight || '400');
      setSelectedFontFamily(currentData.fontFamily || 'Inter');

      // Sync all size variants
      const sizes: ('small' | 'medium' | 'large')[] = ['small', 'medium', 'large'];
      sizes.forEach((size) => {
        const sizeData = currentData[size];
        if (sizeData) {
          const pTop = sizeData.padding?.[0] ?? 0;
          const pRight = sizeData.padding?.[1] ?? 0;
          const pBottom = sizeData.padding?.[2] ?? 0;
          const pLeft = sizeData.padding?.[3] ?? 0;
          const pAllSame = pTop === pRight && pRight === pBottom && pBottom === pLeft;

          const mTop = sizeData.margin?.[0] ?? 0;
          const mRight = sizeData.margin?.[1] ?? 0;
          const mBottom = sizeData.margin?.[2] ?? 0;
          const mLeft = sizeData.margin?.[3] ?? 0;
          const mAllSame = mTop === mRight && mRight === mBottom && mBottom === mLeft;

          const cTop = sizeData.borderRadius?.[0] ?? 0;
          const cRight = sizeData.borderRadius?.[1] ?? 0;
          const cBottom = sizeData.borderRadius?.[2] ?? 0;
          const cLeft = sizeData.borderRadius?.[3] ?? 0;
          const cAllSame = cTop === cRight && cRight === cBottom && cBottom === cLeft;

          setPaddingVals((prev) => ({
            ...prev,
            [size]: {
              all: pAllSame ? pTop : 0,
              top: pTop,
              right: pRight,
              bottom: pBottom,
              left: pLeft,
            },
          }));
          setMarginVals((prev) => ({
            ...prev,
            [size]: {
              all: mAllSame ? mTop : 0,
              top: mTop,
              right: mRight,
              bottom: mBottom,
              left: mLeft,
            },
          }));
          setCornerVals((prev) => ({
            ...prev,
            [size]: {
              all: cAllSame ? cTop : 0,
              top: cTop,
              right: cRight,
              bottom: cBottom,
              left: cLeft,
            },
          }));
        }
      });

      setFontSize(currentData[buttonSize].fontSize);
    }
  }, [currentData]);

  // Calculate Button Type when relevant properties change
  useEffect(() => {
    const corners = cornerVals[buttonSize];
    const padding = paddingVals[buttonSize];

    const allCornersEqual =
      corners.top === corners.right &&
      corners.right === corners.bottom &&
      corners.bottom === corners.left;

    if (allCornersEqual) {
      const buttonHeight = getButtonHeight(padding.top, padding.bottom, fontSize, stroke);
      const newButtonType = getButtonTypeFromRadius(corners.top, buttonHeight);
      if (newButtonType !== buttonType) {
        setButtonType(newButtonType);
      }
    }
  }, [cornerVals, paddingVals, buttonSize, stroke, fontSize]);

  const handleOnChangeButtonType = (value: ButtonType) => {
    setButtonType(value);

    const sizes: ('small' | 'medium' | 'large')[] = ['small', 'medium', 'large'];
    const updatedCornerVals: typeof cornerVals = { ...cornerVals };

    sizes.forEach((size) => {
      const sizeData = currentData[size];
      if (sizeData) {
        let buttonRadius = 0;

        if (value !== 'square') {
          const padding = sizeData.padding;
          const fontSize = sizeData.fontSize;
          const buttonHeight = getButtonHeight(
            padding[0],
            padding[2],
            fontSize,
            currentData.borderWidth,
          );
          if (value === 'round') {
            buttonRadius = Math.round(buttonHeight * 0.2);
          } else if (value === 'pill') {
            buttonRadius = Math.round(buttonHeight * 0.5);
          }
        }

        updatedCornerVals[size] = {
          all: buttonRadius,
          top: buttonRadius,
          right: buttonRadius,
          bottom: buttonRadius,
          left: buttonRadius,
        };
      }
    });

    onEmitUpdate({ cornerVals: updatedCornerVals });

    onTypeChange?.(value);
  };

  const onHandleButtonSizeClick = (value: ButtonSize) => {
    setButtonSize(value);
    const sizeData = currentData[value];
    if (sizeData) {
      const newFontSize = sizeData.fontSize ?? 16;
      setFontSize(newFontSize);
      onEmitUpdate({ buttonSize: value, fontSize: newFontSize });
    }
    onSizeChange?.(value);
  };

  // State for Color objects (supports RGBA, Gradient, or string references)
  const [backgroundColor, setBackgroundColor] = useState<Color | string | null>(null);
  const [textColor, setTextColor] = useState<Color | string>(
    currentData?.fontColor || ({ r: 0, g: 0, b: 0, a: 1 } as Color),
  ); // fontColor is required
  const [fillHoverColor, setFillHoverColor] = useState<Color | string | null>(null);
  const [textHoverColor, setTextHoverColor] = useState<Color | string | null>(null);
  // Border colors don't support gradients, only RGBA or string references
  const [borderColor, setBorderColor] = useState<RGBA | string | null>(null);
  const [borderHoverColor, setBorderHoverColor] = useState<RGBA | string | null>(null);

  // Use the new useColor hook for comprehensive color handling
  const backgroundColorColor = useColor(backgroundColor, styleColorsOrUndefined);
  const textColorColor = useColor(textColor, styleColorsOrUndefined);
  const fillHoverColorColor = useColor(fillHoverColor, styleColorsOrUndefined);
  const textHoverColorColor = useColor(textHoverColor, styleColorsOrUndefined);
  const borderColorColor = useColor(borderColor, styleColorsOrUndefined);
  const borderHoverColorColor = useColor(borderHoverColor, styleColorsOrUndefined);

  // Emit update function - pass only the properties that need to be updated
  const onEmitUpdate = (body: {
    buttonSize?: typeof buttonSize;
    fontSize?: number;
    spacing?: number;
    stroke?: number;
    backgroundColor?: Color | string | null; // Optional, can be null to clear
    borderColor?: RGBA | string | null;
    textColor?: Color | string; // fontColor is required, cannot be null
    currentWeight?: string;
    selectedFontFamily?: string;
    paddingVals?: typeof paddingVals;
    marginVals?: typeof marginVals;
    cornerVals?: typeof cornerVals;
    fillHoverColor?: Color | string | null;
    borderHoverColor?: RGBA | string | null;
    textHoverColor?: Color | string | null;
  }) => {
    // Use provided values or fallback to current state
    const currentButtonSize = body.buttonSize ?? buttonSize;
    const currentFontSize = body.fontSize ?? fontSize;
    const currentSpacing = body.spacing ?? spacing;
    const currentStroke = body.stroke ?? stroke;
    const currentWeightValue = body.currentWeight ?? currentWeight;
    const currentFontFamily = body.selectedFontFamily ?? selectedFontFamily;
    const currentPaddingVals = body.paddingVals ?? paddingVals;
    const currentMarginVals = body.marginVals ?? marginVals;
    const currentCornerVals = body.cornerVals ?? cornerVals;

    // Resolve colors - use provided value if explicitly set (including null), fallback to current state, then to existing data
    const resolvedBgColor =
      'backgroundColor' in body
        ? body.backgroundColor
        : (backgroundColor ?? currentData.backgroundColor);
    const resolvedTextColor =
      'textColor' in body ? body.textColor : (textColor ?? currentData.fontColor);
    const resolvedFillHoverColor =
      'fillHoverColor' in body
        ? body.fillHoverColor
        : (fillHoverColor ?? currentData.backgroundHoverColor);
    const resolvedTextHoverColor =
      'textHoverColor' in body
        ? body.textHoverColor
        : (textHoverColor ?? currentData.fontHoverColor);
    // Border/stroke colors don't support gradients - filter them out
    const resolvedBorderColorRaw =
      'borderColor' in body ? body.borderColor : (borderColor ?? currentData.borderColor);
    const resolvedBorderColor: typeof currentData.borderColor =
      !resolvedBorderColorRaw ||
      (typeof resolvedBorderColorRaw !== 'string' && checkIsGradient(resolvedBorderColorRaw))
        ? null
        : typeof resolvedBorderColorRaw === 'string'
          ? resolvedBorderColorRaw
          : (resolvedBorderColorRaw as unknown as typeof currentData.borderColor);

    const resolvedBorderHoverColorRaw =
      'borderHoverColor' in body
        ? body.borderHoverColor
        : (borderHoverColor ?? currentData.borderHoverColor);
    const resolvedBorderHoverColor: typeof currentData.borderHoverColor =
      !resolvedBorderHoverColorRaw ||
      (typeof resolvedBorderHoverColorRaw !== 'string' &&
        checkIsGradient(resolvedBorderHoverColorRaw))
        ? null
        : typeof resolvedBorderHoverColorRaw === 'string'
          ? resolvedBorderHoverColorRaw
          : (resolvedBorderHoverColorRaw as unknown as typeof currentData.borderHoverColor);

    onUpdate({
      ...currentData,
      // Update shared properties
      fontFamily: currentFontFamily,
      fontWeight: currentWeightValue,
      letterSpacing: currentSpacing,
      borderWidth: currentStroke,
      backgroundColor: resolvedBgColor ?? null,
      borderColor: resolvedBorderColor,
      fontColor: (resolvedTextColor || currentData.fontColor) as typeof currentData.fontColor,
      backgroundHoverColor: resolvedFillHoverColor ?? null,
      borderHoverColor: resolvedBorderHoverColor,
      fontHoverColor: resolvedTextHoverColor ?? null,
      // Update all size variants
      small: {
        ...(currentData.small || {}),
        fontSize:
          currentButtonSize === 'small' ? currentFontSize : (currentData.small?.fontSize ?? 12),
        padding: [
          currentPaddingVals.small.top,
          currentPaddingVals.small.right,
          currentPaddingVals.small.bottom,
          currentPaddingVals.small.left,
        ],
        margin: [
          currentMarginVals.small.top,
          currentMarginVals.small.right,
          currentMarginVals.small.bottom,
          currentMarginVals.small.left,
        ],
        borderRadius: [
          currentCornerVals.small.top,
          currentCornerVals.small.right,
          currentCornerVals.small.bottom,
          currentCornerVals.small.left,
        ],
      },
      medium: {
        ...(currentData.medium || {}),
        fontSize:
          currentButtonSize === 'medium' ? currentFontSize : (currentData.medium?.fontSize ?? 16),
        padding: [
          currentPaddingVals.medium.top,
          currentPaddingVals.medium.right,
          currentPaddingVals.medium.bottom,
          currentPaddingVals.medium.left,
        ],
        margin: [
          currentMarginVals.medium.top,
          currentMarginVals.medium.right,
          currentMarginVals.medium.bottom,
          currentMarginVals.medium.left,
        ],
        borderRadius: [
          currentCornerVals.medium.top,
          currentCornerVals.medium.right,
          currentCornerVals.medium.bottom,
          currentCornerVals.medium.left,
        ],
      },
      large: {
        ...(currentData.large || {}),
        fontSize:
          currentButtonSize === 'large' ? currentFontSize : (currentData.large?.fontSize ?? 20),
        padding: [
          currentPaddingVals.large.top,
          currentPaddingVals.large.right,
          currentPaddingVals.large.bottom,
          currentPaddingVals.large.left,
        ],
        margin: [
          currentMarginVals.large.top,
          currentMarginVals.large.right,
          currentMarginVals.large.bottom,
          currentMarginVals.large.left,
        ],
        borderRadius: [
          currentCornerVals.large.top,
          currentCornerVals.large.right,
          currentCornerVals.large.bottom,
          currentCornerVals.large.left,
        ],
      },
    });
  };

  const fieldWidthClass = 'w-full';

  const handleClick = () => {
    if (onClick) onClick();
  };

  return (
    <div onClick={handleClick} className={clsx('flex max-w-[252px] flex-col gap-3', className)}>
      <div className="flex flex-col gap-3 p-3">
        <p className="pb-1 text-xs font-semibold text-neutral-900">Layout</p>
        <SizeField size={buttonSize} onClick={onHandleButtonSizeClick} text="Size" />
        {/* <div className="my-1 h-0 self-stretch border-t border-neutral-100" /> */}
        <PaddingMarginFields
          label="Margin"
          className="w-full max-w-full"
          value={marginVals[buttonSize]}
          variant={calculateVariant(marginVals[buttonSize])}
          onChange={(vals) => {
            const updated = { ...marginVals, [buttonSize]: vals };
            setMarginVals(updated);
            onEmitUpdate({ marginVals: updated });
          }}
          onChangeComplete={(vals) => {
            const updated = { ...marginVals, [buttonSize]: vals };
            setMarginVals(updated);
            onEmitUpdate({ marginVals: updated });
          }}
          min={0}
        />
        <PaddingMarginFields
          label="Padding"
          className="w-full max-w-full"
          value={paddingVals[buttonSize]}
          variant={calculateVariant(paddingVals[buttonSize])}
          onChange={(vals) => {
            const updated = { ...paddingVals, [buttonSize]: vals };
            setPaddingVals(updated);
            onEmitUpdate({ paddingVals: updated });
          }}
          onChangeComplete={(vals) => {
            const updated = { ...paddingVals, [buttonSize]: vals };
            setPaddingVals(updated);
            onEmitUpdate({ paddingVals: updated });
          }}
          min={0}
        />
        <PaddingMarginFields
          label="Corners"
          className="w-full max-w-full"
          value={cornerVals[buttonSize]}
          variant={calculateVariant(cornerVals[buttonSize])}
          onChange={(vals) => {
            const updated = { ...cornerVals, [buttonSize]: vals };
            setCornerVals(updated);
            onEmitUpdate({ cornerVals: updated });
          }}
          onChangeComplete={(vals) => {
            const updated = { ...cornerVals, [buttonSize]: vals };
            setCornerVals(updated);
            onEmitUpdate({ cornerVals: updated });
          }}
          min={0}
          max={maxRadius}
        />
        {/* Text size within Layout */}
        <SliderField
          label="Text Size"
          value={fontSize}
          onChange={(v) => {
            setFontSize(v);
            onEmitUpdate({ fontSize: v });
          }}
          onChangeComplete={(v) => {
            setFontSize(v);
            onEmitUpdate({ fontSize: v });
          }}
          min={0}
          max={100}
        />
        <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
        <p className="pb-1 text-xs font-semibold text-neutral-900">Button</p>
        <StyleField
          onClick={handleOnChangeButtonType}
          style={buttonType}
          text="Style"
          className="max-w-160px"
        />
        <ColorField
          hasGradient={true}
          canClear={true}
          className={fieldWidthClass}
          inputValue={backgroundColorColor.hexForInput}
          placeholderText="-"
          state="default"
          text="Fill Color"
          color={backgroundColorColor.resolvedColor as Color | undefined}
          gradient={backgroundColorColor.gradient}
          mode={backgroundColorColor.mode}
          palettes={palettesForPicker}
          onChange={(color) => {
            setBackgroundColor(color ?? null);
            onEmitUpdate({ backgroundColor: color ?? null });
          }}
          onChangeComplete={(color) => {
            const colorValue = color ?? null;
            setBackgroundColor(colorValue);
            onEmitUpdate({ backgroundColor: colorValue });
          }}
        />
        {/* Fill Hover */}
        <ColorField
          hasGradient={true}
          canClear={true}
          className={fieldWidthClass}
          inputValue={fillHoverColorColor.hexForInput}
          placeholderText="-"
          state="default"
          text="Fill Hover"
          type="text"
          color={fillHoverColorColor.resolvedColor as Color | undefined}
          gradient={fillHoverColorColor.gradient}
          mode={fillHoverColorColor.mode}
          palettes={palettesForPicker}
          onChange={(color) => {
            setFillHoverColor(color ?? null);
            onEmitUpdate({ fillHoverColor: color ?? null });
          }}
          onChangeComplete={(color) => {
            const colorValue = color ?? null;
            setFillHoverColor(colorValue);
            onEmitUpdate({ fillHoverColor: colorValue });
          }}
        />
        {/* Inline NodeBorderColorSettings UI */}
        <ColorField
          hasGradient={false}
          canClear={true}
          className={fieldWidthClass}
          inputValue={borderColorColor.hexForInput}
          placeholderText="-"
          state="default"
          text="Stroke Color"
          type="text"
          color={borderColorColor.resolvedColor ?? undefined}
          mode={borderColorColor.mode}
          palettes={palettesForPicker}
          onChange={(color) => {
            const value = normalizeBorderColor(color);
            const hadNoColor = !borderColor;
            setBorderColor(value);
            if (hadNoColor && value && stroke === 0) {
              setStroke(1);
              onEmitUpdate({ borderColor: value, stroke: 1 });
            } else {
              onEmitUpdate({ borderColor: value });
            }
          }}
          onChangeComplete={(color) => {
            const value = normalizeBorderColor(color);
            const hadNoColor = !borderColor;
            setBorderColor(value);
            if (hadNoColor && value && stroke === 0) {
              setStroke(1);
              onEmitUpdate({ borderColor: value, stroke: 1 });
            } else {
              onEmitUpdate({ borderColor: value });
            }
          }}
        />
        {/* Stroke Hover */}
        <ColorField
          hasGradient={false}
          canClear={true}
          className={fieldWidthClass}
          inputValue={borderHoverColorColor.hexForInput}
          placeholderText="-"
          state="default"
          text="Stroke Hover"
          type="text"
          color={borderHoverColorColor.resolvedColor ?? undefined}
          mode={borderHoverColorColor.mode}
          palettes={palettesForPicker}
          onChange={(color) => {
            const value = normalizeBorderColor(color);
            const hadNoColor = !borderHoverColor;
            setBorderHoverColor(value);
            if (hadNoColor && value && stroke === 0) {
              setStroke(1);
              onEmitUpdate({ borderHoverColor: value, stroke: 1 });
            } else {
              onEmitUpdate({ borderHoverColor: value });
            }
          }}
          onChangeComplete={(color) => {
            const value = normalizeBorderColor(color);
            const hadNoColor = !borderHoverColor;
            setBorderHoverColor(value);
            if (hadNoColor && value && stroke === 0) {
              setStroke(1);
              onEmitUpdate({ borderHoverColor: value, stroke: 1 });
            } else {
              onEmitUpdate({ borderHoverColor: value });
            }
          }}
        />
        {/* Inline NodeStrokeSettings UI */}
        <SliderField
          label="Stroke"
          value={stroke}
          onChange={(v) => {
            setStroke(v);
            onEmitUpdate({ stroke: v });
          }}
          onChangeComplete={(v) => {
            setStroke(v);
            onEmitUpdate({ stroke: v });
          }}
          min={0}
          max={100}
        />
        <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
        <p className="pb-1 text-xs font-semibold text-neutral-900">Text</p>
        <StylesFontFamilySettings
          value={selectedFontFamily}
          onChange={(family) => {
            setCurrentWeight('400');
            setSelectedFontFamily(family);
            onEmitUpdate({ selectedFontFamily: family, currentWeight: '400' });
          }}
        />
        <StylesFontWeightSettings
          value={currentWeight}
          fontFamily={selectedFontFamily}
          onChange={(weight) => {
            setCurrentWeight(weight);
            onEmitUpdate({ currentWeight: weight });
          }}
        />
        {/* Inline NodeColorSettings UI */}
        <ColorField
          className={fieldWidthClass}
          inputValue={textColorColor.hexForInput}
          placeholderText="-"
          state="default"
          text="Text Color"
          type="text"
          canClear={false}
          color={textColorColor.resolvedColor as Color | undefined}
          gradient={textColorColor.gradient}
          mode={textColorColor.mode}
          palettes={palettesForPicker}
          onChange={(color) => {
            if (color) {
              setTextColor(color);
              onEmitUpdate({ textColor: color });
            }
          }}
          onChangeComplete={(color) => {
            // fontColor is required, so ignore null/undefined
            if (color) {
              setTextColor(color);
              onEmitUpdate({ textColor: color });
            }
          }}
        />
        {/* Text hover color */}
        <ColorField
          hasGradient={true}
          canClear={true}
          className={fieldWidthClass}
          inputValue={textHoverColorColor.hexForInput}
          color={textHoverColorColor.resolvedColor as Color | undefined}
          gradient={textHoverColorColor.gradient}
          mode={textHoverColorColor.mode}
          placeholderText="-"
          state="default"
          text="Text Hover"
          type="text"
          palettes={palettesForPicker}
          onChange={(color) => {
            setTextHoverColor(color ?? null);
            onEmitUpdate({ textHoverColor: color ?? null });
          }}
          onChangeComplete={(color) => {
            const colorValue = color ?? null;
            setTextHoverColor(colorValue);
            onEmitUpdate({ textHoverColor: colorValue });
          }}
        />
        {/* Inline NodeSpaceSettings UI */}
        <SliderField
          label="Spacing"
          value={spacing}
          onChange={(v) => {
            setSpacing(v);
            onEmitUpdate({ spacing: v });
          }}
          onChangeComplete={(v) => {
            setSpacing(v);
            onEmitUpdate({ spacing: v });
          }}
          min={-2}
          max={2}
          step={0.01}
          allowDecimal={true}
        />
      </div>
    </div>
  );
};
